import { useCallback, useEffect, useRef, useState } from 'react';
import { getCustomFieldFilterFieldValues } from '../requests/get-filter-field-values';
import { SelectValueLabel } from '../typings';

interface State {
  items: SelectValueLabel[];
  loading: boolean;
  loadingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  nextSearchAfter: string | null;
  search: string;
}

const initialState: State = {
  items: [],
  loading: false,
  loadingMore: false,
  error: null,
  hasMore: false,
  nextSearchAfter: null,
  search: '',
};

const DEBOUNCE_MS = 250;

export const useFilterFieldValues = (fieldId: string) => {
  const [state, setState] = useState<State>(initialState);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchValues = useCallback(
    async (search: string, searchAfter: string | null) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const isFirstPage = searchAfter === null;
      setState((prev) => ({
        ...prev,
        loading: isFirstPage,
        loadingMore: !isFirstPage,
        error: null,
        search,
      }));
      try {
        const response = await getCustomFieldFilterFieldValues(
          { fieldId, search, searchAfter: searchAfter ?? undefined },
          controller.signal
        );
        if (controller.signal.aborted) return;
        setState((prev) => ({
          ...prev,
          items: isFirstPage ? response.values : [...prev.items, ...response.values],
          loading: false,
          loadingMore: false,
          hasMore: response.hasMore,
          nextSearchAfter: response.nextSearchAfter,
        }));
      } catch (error) {
        if (controller.signal.aborted) return;
        setState((prev) => ({ ...prev, loading: false, loadingMore: false, error: error as Error }));
      }
    },
    [fieldId]
  );

  useEffect(() => {
    fetchValues('', null);
    return () => {
      abortControllerRef.current?.abort();
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [fetchValues]);

  const setSearch = useCallback(
    (nextSearch: string) => {
      setState((prev) => ({ ...prev, search: nextSearch }));
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        fetchValues(nextSearch, null);
      }, DEBOUNCE_MS);
    },
    [fetchValues]
  );

  const fetchMore = useCallback(() => {
    if (state.loading || state.loadingMore || !state.hasMore || !state.nextSearchAfter) return;
    fetchValues(state.search, state.nextSearchAfter);
  }, [fetchValues, state.loading, state.loadingMore, state.hasMore, state.nextSearchAfter, state.search]);

  return {
    items: state.items,
    loading: state.loading,
    loadingMore: state.loadingMore,
    error: state.error,
    hasMore: state.hasMore,
    search: state.search,
    setSearch,
    fetchMore,
  };
};
